import { useCallback, useMemo, useRef } from 'react';

export interface EnergyVisualizerProps {
  /** Array of 100 float values (0.0 to 1.0) representing the energy map */
  energyMap: number[];
  /** Color for the active/played bars */
  primaryColor?: string;
  /** Color for the inactive/unplayed bars */
  backgroundColor?: string;
  /** Thickness of each bar in SVG units (default 2) */
  barWidth?: number;
  /** Space between bars in SVG units (default 1) */
  gap?: number;
  /** Progress value from 0 to 100, determines how many bars use primaryColor */
  progress?: number;
  /** Callback fired on click/scrub with percentage 0-100 */
  onScrub?: (percent: number) => void;
  /** Additional CSS class for the container */
  className?: string;
  /** Container height in CSS units (default '100%') */
  height?: string;
}

/**
 * EnergyVisualizer — A high-performance, reusable React component that renders
 * an energyMap array into an interactive SVG waveform.
 *
 * Bars grow from the vertical center outward (mirrored top/bottom).
 * Fully responsive via SVG viewBox.
 */
export default function EnergyVisualizer({
  energyMap,
  primaryColor = '#00F5FF',
  backgroundColor = 'rgba(255,255,255,0.15)',
  barWidth = 3,
  gap = 1,
  progress = 0,
  onScrub,
  className = '',
  height = '100%',
}: EnergyVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Normalize to exactly 100 bars with amplitude boost
  const bars = useMemo(() => {
    let raw: number[];
    if (!energyMap || energyMap.length === 0) {
      raw = new Array(100).fill(0.1);
    } else if (energyMap.length === 100) {
      raw = energyMap;
    } else {
      // Resample to 100 bars
      raw = [];
      const step = energyMap.length / 100;
      for (let i = 0; i < 100; i++) {
        const idx = Math.floor(i * step);
        raw.push(Math.max(0, Math.min(1, energyMap[idx] ?? 0.1)));
      }
    }
    // Normalize: find the peak and scale all bars so the loudest reaches 1.0
    const peak = Math.max(...raw, 0.01);
    // Apply sqrt curve to boost quieter bars + normalize to peak
    return raw.map(v => {
      const normalized = v / peak;
      // sqrt boosts lower values to be more visible
      return Math.max(0.08, Math.sqrt(normalized));
    });
  }, [energyMap]);

  // Calculate viewBox dimensions
  const totalWidth = useMemo(
    () => bars.length * barWidth + (bars.length - 1) * gap,
    [bars.length, barWidth, gap]
  );
  const viewBoxHeight = 100; // fixed logical height
  const centerY = viewBoxHeight / 2;

  // Determine the progress index (which bar is the last "played" bar)
  const progressIndex = Math.floor((progress / 100) * bars.length);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onScrub || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      onScrub(Math.round(percent));
    },
    [onScrub]
  );

  return (
    <div
      ref={containerRef}
      className={`energy-visualizer ${className}`}
      style={{ width: '100%', height, cursor: onScrub ? 'pointer' : 'default' }}
      onClick={handleClick}
      role="slider"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Audio waveform progress"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${totalWidth} ${viewBoxHeight}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        {bars.map((value, i) => {
          // Use 92% of viewBox height for max bar, minimum 6 units for visibility
          const barHeight = Math.max(6, value * (viewBoxHeight * 0.92));
          const x = i * (barWidth + gap);
          const y = centerY - barHeight / 2;
          const fill = i < progressIndex ? primaryColor : backgroundColor;

          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx={barWidth / 2}
              fill={fill}
            />
          );
        })}
      </svg>
    </div>
  );
}
